import { BaseSyntheticEvent, FormEvent, useCallback, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import {ReactComponent as DeleteIcon} from '../../../images/nde-icon-delete.svg';
import { useDispatch, useSelector } from 'react-redux';
import { selectControls } from '../../../redux/controls';
import { selectMaxTestTakersPerCustomTest } from '../../../redux/appSettings';
import { TextFormControl } from '../../TextFormControl/TextFormControl';
import './CreateRosterModal.scss';
import { CreateRosterModalProps, RosterRequest } from '../models';
import { addRosterStart } from '../../../redux/rosters';

export const CreateRosterModal = ({ open, onClose }: CreateRosterModalProps): JSX.Element => {

  const dispatch = useDispatch();

  const controls = useSelector(selectControls);
  const maxTestTakersPerCustomTest = useSelector(selectMaxTestTakersPerCustomTest);

  ReactModal.setAppElement('#root');

  const createRosterForm = useRef<HTMLFormElement>(null);

  const [isValid, setIsValid] = useState<boolean>(false);

  const submitCreateRosterHandler = useCallback((event: BaseSyntheticEvent, formEl: React.RefObject<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (formEl.current) {
      const formData = new FormData(formEl.current);
      const formProps = Object.fromEntries(formData);
      let rosterName = formProps['roster-name'] as string;
      if (rosterName) {
        rosterName = rosterName.trim();
      }
      const numStudents = formProps['num-of-students'] as string;

      dispatch(
        addRosterStart({
          rosterName,
          numStudents
        })
      );
      onClose({} as RosterRequest);

    }
  }, [dispatch, onClose]);

  const closeModalHandler = useCallback(() => {
    setIsValid(false);
    onClose({} as RosterRequest);
  }, [onClose]);

  const onChangeNumberHandler = useCallback((event: FormEvent<HTMLInputElement>) => {
    const num = +event.currentTarget.value;
    if (!isNaN(num) && num > 0) {
      if (num > maxTestTakersPerCustomTest) {
        event.currentTarget.value = maxTestTakersPerCustomTest.toString();
      }
    } else {
      event.currentTarget.value = '1';
    }
    setIsValid(!!createRosterForm.current?.checkValidity());
  }, [maxTestTakersPerCustomTest]);

  return (
    <>
      {
        open &&
        <ReactModal
          isOpen={open}
          className="nqt-modal"
          // onAfterOpen={() => onAfterOpenCallback(winType, cancelEl, okEl)}
          overlayClassName="nqt-modal-overlay"
          shouldFocusAfterRender={true}
          contentLabel={controls?.RosterStudentRoster?.controlText}
        >
          <form ref={createRosterForm} onSubmit={(e) => submitCreateRosterHandler(e, createRosterForm)} className='nqt-modal--container'>
            <div className="nqt-modal--header-group">
              <header className='nqt-modal--title'>{controls?.RosterStudentRoster?.controlText}</header>
              <button className='nqt-modal--close-button'
                type='button'
                onClick={() => closeModalHandler()}>
                <DeleteIcon/>
                <span className="visually-hidden">{controls?.ModalClose?.controlText}</span>
              </button>
            </div>
            <div className="nqt-modal--border">
              <section className="nqt-acc-modal--content container">
                <div className="nqt-modal--form-controls container-fluid">
                  <div className="row row-cols-1 gy-3">
                    <div className="col container-fluid">
                      <TextFormControl id='create-roster--name'
                        containerClass="nqt-modal--text-input"
                        controlName='roster-name'
                        controlType="text"
                        headerText={controls?.RosterName?.controlText}
                        isValidCallback={() => setIsValid(!!createRosterForm.current?.checkValidity())}
                        pattern="^(?=^[^\s])[\x20-\x7E]{1,128}$"
                        minLength={1}
                        maxLength={128}
                        required={true}
                        setFocus={true}/>
                    </div>
                    <div className="col container-fluid">
                      <TextFormControl id='create-roster--num-of-students'
                        containerClass="nqt-modal--text-input number"
                        controlName='num-of-students'
                        controlType="number"
                        min={1}
                        max={maxTestTakersPerCustomTest}
                        maxLength={3}
                        headerText={controls?.RosterNumStudents?.controlText}
                        onChangeCallback={onChangeNumberHandler}
                        isValidCallback={() => setIsValid(!!createRosterForm.current?.checkValidity())}
                        required={true}/>
                    </div>
                  </div>
                </div>
              </section>
              <hr />
              <div className="nqt-modal--buttons col-9 offset-3">
                <button className="nqt-button--cancel col-4 offset-3"
                  type='button'
                  onClick={() => closeModalHandler()}>{controls?.ModalCancel?.controlText}</button>
                <button className="nqt-button--submit col-4"
                  disabled={!isValid}
                  type="submit"
                  onClick={(e) => submitCreateRosterHandler(e, createRosterForm)}>{controls?.RosterSave?.controlText}</button>
              </div>
            </div>
          </form>
        </ReactModal>
      }
    </>
  );
};

export default CreateRosterModal;